import { Badge, Button, Group, List, Skeleton, Stack, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconCheck } from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { endpoints } from "../api/client";
import { billingQuery, meQuery, useNotifyError } from "../api/queries";
import { AppFrame } from "../components/app-frame";
import { Page, PageHeader } from "../components/page";
import { formatDate } from "../i18n/format";
import { useLocale, useMessages } from "../i18n/provider";
import styles from "./billing.module.css";

export function BillingPage() {
  const t = useMessages();
  const { locale } = useLocale();
  const queryClient = useQueryClient();
  const notifyError = useNotifyError();
  const [params, setParams] = useSearchParams();
  const { data, isLoading } = useQuery(billingQuery());
  const [busy, setBusy] = useState<"month" | "year" | "portal" | null>(null);

  const checkoutStatus = params.get("checkout");

  useEffect(() => {
    if (checkoutStatus !== "success" && checkoutStatus !== "cancelled") return;
    notifications.show({
      color: checkoutStatus === "success" ? "green" : "gray",
      message:
        checkoutStatus === "success"
          ? t("billing.checkoutSuccess")
          : t("billing.checkoutCancelled"),
      autoClose: 5000,
    });
    setParams({}, { replace: true });
    if (checkoutStatus === "success") {
      void queryClient.invalidateQueries({ queryKey: billingQuery().queryKey });
      void queryClient.invalidateQueries({ queryKey: meQuery.queryKey });
    }
  }, [checkoutStatus, setParams, queryClient, t]);

  const startCheckout = async (interval: "month" | "year") => {
    setBusy(interval);
    try {
      const result = await endpoints.checkout(interval);
      window.location.assign(result.url);
    } catch (error) {
      notifyError(error);
      setBusy(null);
    }
  };

  const openPortal = async () => {
    setBusy("portal");
    try {
      const result = await endpoints.portal();
      window.location.assign(result.url);
    } catch (error) {
      notifyError(error);
      setBusy(null);
    }
  };

  const pro = data?.plan === "pro";

  return (
    <AppFrame active="/billing">
      <Page width="narrow">
        <PageHeader title={t("billing.title")} />

        {isLoading || !data ? (
          <Stack gap="md">
            <Skeleton height={180} radius="lg" />
            <Skeleton height={180} radius="lg" />
          </Stack>
        ) : (
          <div className={styles.plans}>
            <section className={styles.plan} data-current={!pro || undefined}>
              <Group justify="space-between">
                <Text fw={600}>{t("billing.freePlan")}</Text>
                {!pro ? (
                  <Badge variant="light" color="gray" size="sm">
                    {t("billing.currentPlan")}
                  </Badge>
                ) : null}
              </Group>
              <div className={styles.price}>{t("billing.freePlan")}</div>
              <Text size="sm" c="dimmed">
                {t("billing.freeBody")}
              </Text>
              <List size="sm" spacing={6} mt="md" icon={<IconCheck size={14} stroke={2} />}>
                <List.Item>{t("landing.freeFeature1")}</List.Item>
                <List.Item>{t("landing.freeFeature2")}</List.Item>
                <List.Item>{t("landing.freeFeature3")}</List.Item>
                <List.Item>{t("landing.freeFeature4")}</List.Item>
              </List>
            </section>

            <section className={styles.plan} data-current={pro || undefined}>
              <Group justify="space-between">
                <Text fw={600}>{t("billing.proTitle")}</Text>
                {pro ? (
                  <Badge size="sm">{t("billing.currentPlan")}</Badge>
                ) : (
                  <Badge variant="light" color="blue" size="sm">
                    {t("common.pro")}
                  </Badge>
                )}
              </Group>
              <div className={styles.price}>
                {t("billing.monthlyPrice")}
                <span>{t("billing.perMonth")}</span>
              </div>
              <Text size="sm" c="dimmed">
                {t("billing.proBody")}
              </Text>
              <List size="sm" spacing={6} mt="md" icon={<IconCheck size={14} stroke={2} />}>
                <List.Item>{t("landing.proFeature1")}</List.Item>
                <List.Item>{t("landing.proFeature2")}</List.Item>
                <List.Item>{t("landing.proFeature3")}</List.Item>
                <List.Item>{t("landing.proFeature4")}</List.Item>
              </List>

              {pro && data.entitlementSource === "organization" ? (
                <Stack gap={8} mt="lg">
                  <Text size="sm" c="dimmed">
                    {t("billing.viaOrganization")}
                  </Text>
                  <Button variant="light" component={Link} to="/organization">
                    {t("organization.manage")}
                  </Button>
                </Stack>
              ) : pro ? (
                <Stack gap={8} mt="lg">
                  {data.status ? (
                    <Text size="sm" c="dimmed">
                      {t("billing.status")}: {data.status}
                    </Text>
                  ) : null}
                  {data.currentPeriodEnd ? (
                    <Text size="sm" c="dimmed">
                      {data.cancelAtPeriodEnd
                        ? t("billing.ends", {
                            date: formatDate(data.currentPeriodEnd, locale),
                          })
                        : t("billing.renews", {
                            date: formatDate(data.currentPeriodEnd, locale),
                          })}
                    </Text>
                  ) : null}
                  {data.portalAvailable ? (
                    <Button
                      variant="light"
                      onClick={() => void openPortal()}
                      loading={busy === "portal"}
                    >
                      {t("billing.manage")}
                    </Button>
                  ) : (
                    <Text size="xs" c="dimmed">
                      {t("billing.portalUnavailable")}
                    </Text>
                  )}
                </Stack>
              ) : (
                <Stack gap={8} mt="lg">
                  <Button
                    onClick={() => void startCheckout("month")}
                    loading={busy === "month"}
                    disabled={busy !== null}
                  >
                    {t("billing.subscribe")} · {t("billing.monthlyPrice")}
                  </Button>
                  <Button
                    variant="light"
                    onClick={() => void startCheckout("year")}
                    loading={busy === "year"}
                    disabled={busy !== null}
                  >
                    {t("billing.yearlyPrice")} · {t("common.year")} · {t("common.savePercent")}
                  </Button>
                </Stack>
              )}
            </section>
          </div>
        )}

        <section className={styles.team}>
          <div>
            <Text fw={600}>{t("billing.teamTitle")}</Text>
            <Text size="sm" c="dimmed" mt={4}>
              {t("billing.teamBody")}
            </Text>
          </div>
          <Button variant="light" component={Link} to="/organization">
            {t("billing.teamCta")}
          </Button>
        </section>
      </Page>
    </AppFrame>
  );
}
